'use client';

import React from 'react';
import Link from '@ui/Link';
import Dialog from '@ui/Dialog';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import NekiLogo from '../NekiLogo';
import Typography from '@ui/Typography';
import { ScrollArea } from '@ui/ScrollArea';
import useConnectModalStore from '@/components/store/connectModalSlice';
import useCompanyConnectPopUpStore from '@/components/store/companyConnectPopUpSlice';
import OrgType from '@/constants/orgType.constant';
import { getEnvBaseUrl, getLoginUrlByOrgId } from '@/utils/stringHelpers';
import { DOMAIN_PREFIX_REDIRECTS } from '@/constants/orgConfig.constant';

function CompanyConnectPopUp(props) {
  const isOpen = useCompanyConnectPopUpStore((state) => state.open);
  const setOpen = useCompanyConnectPopUpStore((state) => state.setOpen);
  const company = useCompanyConnectPopUpStore((state) => state.company);
  const setShowConnectModal = useConnectModalStore((state) => state.setOpen);

  const nekiBaseUrl = getEnvBaseUrl();

  function closeModal() {
    setOpen(false);
  }

  function handleConnect() {
    setShowConnectModal(true, company);
  }

  const clearCookie = (name) => {
    const path = '/'; // Adjust if needed
    const domain = ''; // Adjust if needed
    const secure = false; // Set to true if your site uses HTTPS

    if (document) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; ${
        secure ? 'secure; ' : ''
      }SameSite=Lax;`;
    }
  };

  function handleSignUpRedirect() {
    const loginUrlByHostName =
      DOMAIN_PREFIX_REDIRECTS[window.location.hostname];
    const loginUrlByOrgId = getLoginUrlByOrgId(company?.id);
    const loginUrl = loginUrlByHostName || loginUrlByOrgId;
    if (loginUrl) {
      return loginUrl;
    }
    clearCookie('appSession');
    const signUpPath = `/onboard/community-user?communityId=${company.id}`;
    const signUpRedirectLink = `${nekiBaseUrl}/api/auth/signup?returnTo=${encodeURIComponent(
      signUpPath,
    )}`;
    return signUpRedirectLink;
  }

  function handleLogInRedirect() {
    const loginUrlByHostName =
      DOMAIN_PREFIX_REDIRECTS[window.location.hostname];
    const loginUrlByOrgId = getLoginUrlByOrgId(company?.id);
    const loginUrl = loginUrlByHostName || loginUrlByOrgId;
    if (loginUrl) {
      return loginUrl;
    }
    clearCookie('appSession');
    const loginLink = `${nekiBaseUrl}/api/auth/logout`;
    return loginLink;
  }

  // const loginLink = `${nekiBaseUrl}/api/auth/login`;

  if (!company) return null;

  const isChangeMakerCommunity = company?.orgType === OrgType.ChangeMakers;

  return (
    <Dialog isOpen={isOpen} onClose={closeModal} maxWidth="md">
      <ScrollArea className="w-full flex-1">
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <div className="rounded-full border-4 border-gray-50">
            <Avatar
              src={company.logoUrl}
              alt={company.name}
              profile="company"
              size="lg"
              shape="circle"
            />
          </div>

          {isChangeMakerCommunity ? (
            <>
              <Typography variant="h2" component="h2" className="text-center">
                Sign Up on Neki to Join {company.name || 'Community'}
              </Typography>
              <Button
                as="a"
                variant="filled"
                color="primary"
                href={handleSignUpRedirect()}
                target="_blank"
                className="px-5">
                Sign Up
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h2" component="h2" className="text-center">
                Connect with {company.name || 'us'} to explore social impact
                opportunities
              </Typography>
              <Button
                variant="filled"
                color="primary"
                onClick={handleConnect}
                className="px-5">
                Connect
              </Button>
            </>
          )}

          <Typography variant="body1">
            Already have an account on Neki?{' '}
            <Typography
              component="a"
              href={handleLogInRedirect()}
              variant="body1"
              className="text-text-hyperlink hover:underline">
              Log In
            </Typography>
          </Typography>
          <div className="flex items-center justify-center">
            <Typography variant="caption" className="font-medium">
              Powered by
            </Typography>
            <NekiLogo className="!h-4" />
          </div>
        </div>
      </ScrollArea>
    </Dialog>
  );
}

export default CompanyConnectPopUp;
